// components/Footer.jsx
import React from "react";

import { Facebook, Instagram, Youtube } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToCampaigns = () => {
    setTimeout(() => {
      const campaignsSection = document.getElementById("campaigns");
      if (campaignsSection) {
        campaignsSection.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 100);
  };

  const handleCampaignsClick = (e) => {
    e.preventDefault();

    if (location.pathname === "/") {
      scrollToCampaigns();
    } else {
      navigate("/");
      scrollToCampaigns();
    }
  };

  const footerLinks = {
  "Quick Links": [
    { label: "Home", path: "/" },
    {
      label: "Campaigns",
      path: "/",
      onClick: handleCampaignsClick,
      isButton: true,
    },
    { label: "Start Fundraiser", path: "/start-fundraiser" },
  ],
  Account: [
    { label: "Sign In", path: "/signin" },
    { label: "Register", path: "/signin" },
  ],
  Legal: [
    { label: "Privacy Policy", path: "/privacy-policy" },
    { label: "Terms & Conditions", path: "/terms-and-conditions" },
  ],
};
  const socialIcons = [
    {
      IconComponent: Facebook,
      label: "Facebook",
      link: "https://www.facebook.com/share/1NXQTRboXz/",
    },
    {
      IconComponent: Instagram,
      label: "Instagram",
      link: "https://www.instagram.com/donatepeindia?utm_source=qr&igsh=NGFnc2Q1Z2w0YnBw",
    },
    {
      IconComponent: Youtube,
      label: "YouTube",
      link: "https://www.youtube.com/@Donatepeindia",
    },
  ];

  return (
    <>
      {/* Simple CTA Banner */}
     

      {/* Main Footer */}
      <footer className="bg-transparent py-8 md:py-10">
        <div className="max-w-7xl mx-auto px-4">
          {/* Top Section */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-10 pb-8 md:pb-10 items-start">
            {/* Brand Section */}
            <div className="space-y-4 md:col-span-1">
              <Link to="/" className="inline-block">
                <div>
                  <span className="text-black font-bold text-3xl">
                    DonatePe
                  </span>
                  <p className="text-gray-700 text-sm">Compassionate giving</p>
                </div>
              </Link>

              <p className="text-gray-700 text-sm leading-relaxed max-w-sm">
                Empowering people to create positive change through secure and
                transparent giving.
              </p>
            </div>

            {/* Links Section */}
            <div className="grid grid-cols-2 md:grid-cols-3 col-span-1 md:col-span-2 gap-6">
              {Object.entries(footerLinks).map(([category, links]) => (
                <div key={category} className="space-y-4">
                  <h4 className="font-bold text-black text-base">{category}</h4>

                  <div className="flex flex-col gap-3">
                    {links.map((link) => (
                      <div key={link.label}>
                        {link.isButton ? (
                          <button
                            onClick={link.onClick}
                            className="text-gray-700 hover:text-black text-sm transition-colors font-normal text-left"
                          >
                            {link.label}
                          </button>
                        ) : (
                          <Link
                            to={link.path}
                            className="text-gray-700 hover:text-black text-sm transition-colors font-normal block"
                          >
                            {link.label}
                          </Link>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Social Media & Payment Methods */}
            <div className="space-y-4 md:justify-self-end md:text-right md:col-span-1">
              <h4 className="font-bold text-black text-base text-center md:text-right">Connect with us</h4>

              <div className="flex gap-3 justify-center md:justify-end">
                {socialIcons.map(({ IconComponent, label, link }) => (
                  <a
                    key={label}
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-200 text-black hover:bg-black hover:text-white transition-colors"
                    aria-label={label}
                  >
                    <IconComponent className="w-5 h-5" />
                  </a>
                ))}
              </div>

              {/* Payment Methods Section */}
              <div className="mt-6 pt-4 border-t border-gray-200">
  <h4 className="font-bold text-black text-base mb-3 text-center md:text-right">
    We Accept
  </h4>
  <div className="flex flex-wrap items-center justify-center md:justify-end gap-2">
    {/* VISA */}
    <div className="relative group" title="VISA">
      <img
        src="/assets/visa.png"
        alt="VISA"
        className="h-6 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity duration-300"
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = "https://via.placeholder.com/30x18?text=VISA";
        }}
      />
      <span className="absolute top-full mt-1 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[8px] py-1 px-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
        VISA
      </span>
    </div>

    {/* Mastercard */}
    <div className="relative group" title="Mastercard">
      <img
        src="/assets/mastercard_logo.jpg"
        alt="Mastercard"
        className="h-6 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity duration-300"
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = "https://via.placeholder.com/30x18?text=MC";
        }}
      />
      <span className="absolute top-full mt-1 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[8px] py-1 px-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
        Mastercard
      </span>
    </div>

    {/* American Express */}
    <div className="relative group" title="American Express">
      <img
        src="/assets/amex.png"
        alt="American Express"
        className="h-6 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity duration-300"
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = "https://via.placeholder.com/30x18?text=AMEX";
        }}
      />
      <span className="absolute top-full mt-1 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[8px] py-1 px-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
        Amex
      </span>
    </div>

    {/* RuPay */}
    <div className="relative group" title="RuPay">
      <img
        src="/assets/rupay.png"
        alt="RuPay"
        className="h-6 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity duration-300"
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = "https://via.placeholder.com/30x18?text=RuPay";
        }}
      />
      <span className="absolute top-full mt-1 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[8px] py-1 px-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
        RuPay
      </span>
    </div>

    {/* Paytm */}
    <div className="relative group" title="Paytm">
      <img
        src="/assets/paytm.svg"
        alt="Paytm"
        className="h-6 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity duration-300"
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = "https://via.placeholder.com/30x18?text=Paytm";
        }}
      />
      <span className="absolute top-full mt-1 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[8px] py-1 px-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
        Paytm
      </span>
    </div>

    {/* UPI */}
    <div className="relative group" title="UPI">
      <img
        src="/assets/upi.png"
        alt="UPI"
        className="h-6 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity duration-300"
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = "https://via.placeholder.com/30x18?text=UPI";
        }}
      />
      <span className="absolute top-full mt-1 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[8px] py-1 px-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
        UPI
      </span>
    </div>
  </div>
  
  {/* Payment Description */}
  <p className="text-gray-500 text-[10px] mt-4 text-center md:text-right">
    Secure payments • 100% encrypted
  </p>
</div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-200 pt-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-center md:text-left">
                <p className="text-gray-700 text-sm">
                  © {new Date().getFullYear()} DonatePe. All rights reserved.
                </p>
                <p className="text-gray-700 text-xs mt-1">
                  A compassionate giving platform
                </p>
              </div>

              <div className="flex gap-4">
                <Link
                  to="/privacy-policy"
                  className="text-gray-700 hover:text-black text-sm transition-colors"
                >
                  Privacy
                </Link>
                <Link
                  to="/terms-and-conditions"
                  className="text-gray-700 hover:text-black text-sm transition-colors"
                >
                  Terms
                </Link>
                <Link
                  to="/contact"
                  className="text-gray-700 hover:text-black text-sm transition-colors"
                >
                  Contact
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;